import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  ViewEncapsulation
} from '@angular/core';
import { AsyncPipe, I18nPluralPipe } from '@angular/common';
import { map, Observable } from 'rxjs';

import { addLeadingZero, Countdown, countdown } from '@core/helpers';
import { days, games } from '@core/common/plurals';
import { Notifier } from '@core/notifications';
import { ConvertDirective } from '@core/converter';
import { GameMode } from '@core/common';
import { CounterValue } from '@app/modules/users/users.entity';

import { CardVariation, CounterType } from './constants';

const ALERT_IS_SOON = $localize`:@@main.mode_card.alert_is_soon:Режим еще не доступен`;

type Timer = {
  days: number;
  hours: string;
  minutes: string;
  seconds: string;
};

@Component({
    selector: 'main-mode-card[gameMode]',
    templateUrl: './mode-card.component.html',
    styleUrls: [
        './mode-card.component.scss',
        './mode-card-crash.component.scss',
        './mode-card-support.component.scss'
        // './mode-card-xmas.component.scss',
    ],
    host: {
        '[class]': `'custom-transition-bg mode-card mode-card_'+ gameMode`,
        '[class.mode-card_mini]': 'cardVariation === CardVariation.MINI',
        '[class.mode-card_toolbar]': 'cardVariation === CardVariation.TOOLBAR',
        '[class.no-animation]': 'disableAnimation',
        '(click)': 'onCardClick()'
    },
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    imports: [
        ConvertDirective,
        AsyncPipe,
        I18nPluralPipe
    ]
})
export class ModeCardComponent {
  private readonly notifier = inject(Notifier);
  
  @Input()
  public cardTitle!: string;

  @Input()
  public gameMode!: GameMode|'support'|'xmas';

  @Input()
  public cardVariation?: CardVariation;

  @Input()
  public isSoon = false;

  @Input()
  public isHot = false;

  @Input()
  public isNew = false;

  @Input()
  public disableAnimation = false;

  @Input()
  public showLink = true;

  @Input()
  public badge?: string;

  @Input()
  public counter?: CounterValue;

  @Input()
  public counterType: CounterType = CounterType.USERS;

  @Input()
  set countDown(endAt: string | null) {
    if (!endAt) {
      return;
    }

    this.countdown$ = this.createCountdown(endAt);
  }

  public readonly CardVariation = CardVariation;
  public readonly CounterType = CounterType;
  public readonly daysPlurals = days;
  public readonly gamesPlurals = games;

  public countdown$!: Observable<Timer>;

  onCardClick(): void {
    this.isSoon && this.notifier.warning(ALERT_IS_SOON);
  }

  private createCountdown(endAt: string): Observable<Timer> {
    return countdown(endAt).pipe(
      map((cd: Countdown) => {
        const hours = addLeadingZero(cd.duration!.hours(), false);
        const minutes = addLeadingZero(cd.duration!.minutes(), false);
        const seconds = addLeadingZero(cd.duration!.seconds(), false);

        return {
          days: cd.duration!.days(),
          hours,
          minutes,
          seconds
        };
      })
    );
  }
}
